import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { message } from 'antd';
import { authService } from '../services/authService';

interface User {
  id: number;
  username?: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  defaultWarehouseId?: number;
  /** Silme izni; false ise tüm sayfalarda Sil butonu gizlenir. */
  canDelete?: boolean;
  /** Servis fişi iptal (rollback) izni; ADMIN her zaman iptal edebilir. */
  canCancelServiceOrder?: boolean;
  roles: string[];
  roleCodes?: string[];
  permissions: string[];
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (phoneNumber: string, password: string) => Promise<boolean>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      
      if (storedToken && storedUser) {
        // Token ve user bilgisi varsa, direkt state'e yükle
        // Not: Production'da token validate endpoint'i kullanılmalı
        const parsed = JSON.parse(storedUser);
        if (parsed.canDelete === undefined) parsed.canDelete = true;
        if (parsed.canCancelServiceOrder === undefined) parsed.canCancelServiceOrder = false;
        setToken(storedToken);
        setUser(parsed);
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const setTokenAttr = (newToken: string | null) => {
    setToken(newToken);
    if (newToken) {
      localStorage.setItem('token', newToken);
    } else {
      localStorage.removeItem('token');
    }
  };

  const login = async (phoneNumber: string, password: string): Promise<boolean> => {
    try {
      const response = await authService.login(phoneNumber, password);
      setTokenAttr(response.token);
      setUser(response.user);
      localStorage.setItem('user', JSON.stringify(response.user));
      message.success('Giriş başarılı');
      return true;
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Giriş başarısız');
      return false;
    }
  };

  const logout = () => {
    if (token) {
      authService.logout(token).catch(console.error);
    }
    setUser(null);
    setTokenAttr(null);
    localStorage.removeItem('user');
    message.info('Çıkış yapıldı');
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
